import { useMemo, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import ExecutiveSummaryGenericTable from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryGenericTable/ExecutiveSummaryGenericTable';
import { IncomeCalculationType } from 'constants/proForma';
import { IProFormaSocketRoomData } from 'views/ProFormaTable/types';
import { WattsType } from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { useInvestmentMemoContext } from 'views/InvestmentMemo/InvestmentMemoContext';
import { InvestmentMemoFormData } from '../../types';
import { MEMO_SECTIONS, FINANCIALS_FORMS, FINANCIALS_FORM_NAMES } from '../../constants';
import '../InvestmentMemoSections.scss';
import {
    formatDevelopment,
    formatAssumptions,
    formatForecast,
    formatReturns,
    formatOperations,
} from '../../helpers';

interface IInvestmentMemoFinancialsProps {
    updateFormData: (section: keyof InvestmentMemoFormData, data: Partial<InvestmentMemoFormData[keyof InvestmentMemoFormData]>) => void;
    proFormaRoom?: IProFormaSocketRoomData;
}

const InvestmentMemoFinancials = ({
    updateFormData,
    proFormaRoom,
}: IInvestmentMemoFinancialsProps) => {
    const { selectedProForma, isLoadingProForma } = useInvestmentMemoContext();

    const disposition = proFormaRoom?.disposition;
    const returnsData = proFormaRoom?.returns;
    const proForma = proFormaRoom?.generalInfo || { proFormaModelId: undefined };
    const incomeSummary = proFormaRoom?.incomeSummary;
    const operatingInfo = proFormaRoom?.operatingInfo;
    const permanentDebtInfo = proFormaRoom?.permanentDebtsInfo?.[0];
    const expenses = proFormaRoom?.expenseSummary;
    const wattsType = WattsType.KILOWATTS;

    const formattedDevelopment = useMemo(
        () => (proForma.proFormaModelId
            ? formatDevelopment(proForma.proFormaModelId, proFormaRoom?.usesSummary, proFormaRoom?.sourcesSummary)
            : []),
        [proForma.proFormaModelId, proFormaRoom?.usesSummary, proFormaRoom?.sourcesSummary],
    );

    const formattedAssumptions = useMemo(
        () => (proForma.proFormaModelId && incomeSummary && operatingInfo && expenses
            ? formatAssumptions(
                proForma.proFormaModelId,
                incomeSummary,
                operatingInfo,
                expenses,
                wattsType,
            )
            : []),
        [proForma.proFormaModelId, incomeSummary, operatingInfo, expenses, wattsType],
    );

    const formattedForecast = useMemo(
        () => (disposition && operatingInfo && permanentDebtInfo && returnsData
            ? formatForecast(disposition, operatingInfo, permanentDebtInfo, returnsData)
            : null),
        [disposition, operatingInfo, permanentDebtInfo, returnsData],
    );

    const formattedOperations = useMemo(
        () => (disposition && proForma.proFormaModelId && operatingInfo
            ? formatOperations(
                proForma.proFormaModelId,
                disposition,
                operatingInfo.incomeCalculationType as IncomeCalculationType,
                wattsType,
            )
            : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [proForma.proFormaModelId, disposition, operatingInfo?.incomeCalculationType, wattsType],
    );

    const formattedReturns = useMemo(
        () => (returnsData && Object.keys(returnsData).length && proFormaRoom?.waterfallAnalysis
            ? formatReturns(returnsData, proFormaRoom.waterfallAnalysis)
            : null),
        [returnsData, proFormaRoom?.waterfallAnalysis],
    );

    // Sync formatted data to form state whenever it changes
    useEffect(() => {
        updateFormData('financials', {
            development: formattedDevelopment,
            assumptions: formattedAssumptions,
            forecast: formattedForecast,
            operations: formattedOperations,
            returns: formattedReturns,
        });
    }, [
        formattedDevelopment,
        formattedAssumptions,
        formattedForecast,
        formattedOperations,
        formattedReturns,
        updateFormData,
    ]);

    return (
        <div className="d-flex justify-content-center pb-2 px-2">
            <div className="w-75">
                <div>
                    <h2 className="text-dark">{MEMO_SECTIONS[2].TITLE}</h2>
                    <p>{MEMO_SECTIONS[2].DESCRIPTION}</p>
                </div>

                <div className="d-flex flex-column mb-2">
                    {FINANCIALS_FORMS.map(card => (
                        <div key={card.id}>
                            <ExpandableSection
                                number={card.number}
                                title={card.title}
                                subtitle={card.subtitle}
                                animate
                                disableExpandingInReadOnly
                                isExpanded={Boolean(selectedProForma)}
                            >
                                <RenderIf
                                    isTrue={Boolean(selectedProForma)}
                                    isLoading={isLoadingProForma}
                                    loadingMessage="Loading Northspyre AI..."
                                    shouldLoadAiLogo
                                >
                                    <RenderIf isTrue={card.id === FINANCIALS_FORM_NAMES.SOURCES_USES}>
                                        <RenderIf isTrue={formattedDevelopment?.length}>
                                            <div className="p-3">
                                                <ExecutiveSummaryGenericTable
                                                    title="DEVELOPMENT"
                                                    sections={formattedDevelopment}
                                                />
                                            </div>
                                        </RenderIf>
                                    </RenderIf>

                                    <RenderIf isTrue={card.id === FINANCIALS_FORM_NAMES.FINANCIAL_SUMMARY}>
                                        <div className="d-flex flex-column">
                                            <RenderIf isTrue={formattedAssumptions?.length}>
                                                <div className="px-3 pt-3 pb-1 mb-2">
                                                    <Row className="mx-n1">
                                                        <Col className="px-1" sm={12}>
                                                            <ExecutiveSummaryGenericTable
                                                                title="ASSUMPTIONS"
                                                                sections={formattedAssumptions}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </RenderIf>

                                            <RenderIf isTrue={formattedForecast && formattedForecast.length}>
                                                <div className="px-3 py-1 mb-2 pb-1">
                                                    <Row className="mx-n1">
                                                        <Col className="px-1" sm={12}>
                                                            <ExecutiveSummaryGenericTable
                                                                title="FORECAST"
                                                                sections={formattedForecast!}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </RenderIf>

                                            <RenderIf isTrue={formattedOperations && formattedOperations.length}>
                                                <div className="px-3 py-1 mb-2 pb-1">
                                                    <Row className="mx-n1">
                                                        <Col className="px-1" sm={12}>
                                                            <ExecutiveSummaryGenericTable
                                                                title="OPERATIONS"
                                                                sections={formattedOperations!}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </RenderIf>

                                            <RenderIf isTrue={formattedReturns && formattedReturns.length}>
                                                <div className="px-3 mb-2 pb-2">
                                                    <Row className="mx-n1">
                                                        <Col className="px-1" sm={12}>
                                                            <ExecutiveSummaryGenericTable
                                                                title="RETURNS"
                                                                sections={formattedReturns!}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </RenderIf>
                                        </div>
                                    </RenderIf>
                                </RenderIf>
                            </ExpandableSection>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default InvestmentMemoFinancials;
